import { useCallback, useRef } from 'react';

import { FirebaseError } from 'firebase/app';
import type { User } from 'firebase/auth';
import { useTranslation } from 'react-i18next';

import { analytics } from '../../services/analytics';
import type { AuthParams } from '../../services/auth';
import { auth } from '../../services/auth';
import { sendGoogleTagSignUpClick } from '../../services/googleTagEvent';
import { trackShellSignUpClick } from '../../services/nativeBridge/shellAppsFlyer';
import { isInShell } from '../../services/nativeBridge/shellPresence';
import { captureSeonAuthSession } from '../../services/seon/seonAuthSession';
import { useStores } from '../../stores/useStores';
import { getErrorMessage } from '../../utils/errors';
import { getEnvironment } from '../../utils/getEnvironment';

import { isForcePasswordReset } from './forcePasswordReset';
import { sendLoginError } from './loginAnalytics';

export type AuthAction = 'log_in' | 'sign_up';
export type AuthProvider = 'apple' | 'google' | 'facebook' | 'email';

export interface Credentials {
  email: string;
  password: string;
}

export function useHandleAuth(onError: (error: string) => void, defaultNextStep?: () => void) {
  const { authStore, portalStore } = useStores();
  const { t } = useTranslation();

  const landingKey = authStore.getLandingKey();
  // Shell only: the SEON capture can take seconds before any pending flag disables the provider buttons,
  // and a second tap in that window would open a second native sign-in sheet.
  const inFlight = useRef(false);

  const runAuth = useCallback(
    async (
      params: AuthParams & { nextStep?: () => void },
    ): Promise<{
      success: boolean;
      error: string | null;
      user: User | null;
      forceReset?: boolean;
    }> => {
      auth.saveAuthParams(params);
      await captureSeonAuthSession(params.action);
      const { provider, action, credentials, nextStep: customNextStep } = params;
      const nextStep = customNextStep || defaultNextStep;
      let viaNativeShell = false;
      let email = credentials?.email || '';

      try {
        auth.clearStatuses();

        if (authStore.isAuthenticationFlowDone) {
          authStore.setIsAuthenticationFlowDone(false);
        }

        authStore.setLastLoginTabId();
        if (action === 'sign_up') {
          sendGoogleTagSignUpClick({
            provider,
            email: credentials?.email,
          });
          trackShellSignUpClick(provider);
        }
        if (provider === 'email') {
          if (email && getEnvironment() === 'production') {
            const [left, right] = email.split('@');

            const isAllowedPlusInEmail = ['sunfltd.com', 'crowncoinscasino.com'].includes(right);

            if (!isAllowedPlusInEmail) {
              email = left.replace(/\+.+/, '+') + '@' + right;
            }
          }
          const data: Credentials = {
            email,
            password: credentials?.password || '',
          };
          let user;
          if (action === 'sign_up') {
            user = await authStore.signUpWithEmail(data);
          } else {
            user = await authStore.signInWithEmail(data);
          }
          if (!user.emailVerified && typeof nextStep === 'function') {
            nextStep();
          } else {
            authStore.setAuthCompleted();
            portalStore.closePortal();
          }
          return {
            success: true,
            error: null,
            user,
          };
        }
        // `authWithProvider` takes the shell's native sheet inside the mobile shell — see its comment.
        viaNativeShell = isInShell();
        const user = await authStore.authWithProvider(provider);

        // A dismissed native sheet changes no auth state, so nothing else would flip this back.
        if (viaNativeShell && !user) {
          authStore.setIsAuthenticationFlowDone(true);
        }

        return {
          success: true,
          error: null,
          user,
        };
      } catch (e) {
        const forceReset = provider === 'email' && action === 'log_in' && (await isForcePasswordReset(e, email));
        authStore.setIsAuthenticationFlowDone(true);
        // A native-shell failure that is not Firebase's own — the provider SDK failed, the shell never
        // answered, the credential would not decode — carries internal text ("Bridge request timed
        // out…", a failure reason) that means nothing to a player. Firebase's errors keep their mapping.
        const msg = forceReset
          ? t('auth.forceReset.message')
          : viaNativeShell && !(e instanceof FirebaseError)
            ? t('common.error.somethingWentWrongTryAgain')
            : getErrorMessage(e);

        if (provider === 'email' && action === 'log_in') {
          sendLoginError({ error: e, forceReset, errorMessage: msg });
        }

        if (action === 'sign_up') {
          void analytics.sendEvent('sign_up', {
            step_name: 'sign_up',
            event_action: 'validation',
            sign_up_platform: provider,
            is_succeeded: 0,
            user_id: '',
            landingKey,
          });
        }
        onError(msg);

        return {
          success: false,
          error: msg,
          user: null,
          forceReset,
        };
      }
    },
    [authStore, defaultNextStep, portalStore, onError, landingKey, t],
  );

  const handleAuth = useCallback(
    async (
      params: AuthParams & { nextStep?: () => void },
    ): Promise<{
      success: boolean;
      error: string | null;
      user: User | null;
      forceReset?: boolean;
    }> => {
      if (!isInShell()) {
        return runAuth(params);
      }
      if (inFlight.current) {
        return { success: false, error: null, user: null };
      }
      inFlight.current = true;

      try {
        return await runAuth(params);
      } finally {
        inFlight.current = false;
      }
    },
    [runAuth],
  );

  return {
    handleAuth,
  };
}
